'use client'

import { Check } from 'lucide-react'
import Link from 'next/link'

import { META } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * One box for both documents.
 *
 * The payload carries `acceptedTerms` and `acceptedPrivacy` separately, and this
 * control sets both from a single tick. That is deliberate rather than lazy: the
 * two are accepted together at the same moment for the same order, and offering
 * two boxes would imply one could be declined — which the checkout does not
 * allow and the server does not accept.
 *
 * The links open in a NEW TAB, and their clicks stop propagating. Without both,
 * reading what you are agreeing to either toggles the box you just ticked or
 * navigates away from a filled basket and a typed address.
 */
export function ConsentCheckbox({
  checked,
  onChange,
  locale,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  locale: string
}) {
  const legal = 'font-medium text-white/85 underline-offset-4 hover:text-white hover:underline'

  return (
    <label
      className={cn(
        META,
        'mt-1 flex cursor-pointer items-start gap-3 leading-relaxed text-white/65',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors',
          checked ? 'border-white bg-white text-neutral-900' : 'border-white/25 bg-white/[0.04]',
        )}
      >
        {checked ? <Check size={12} strokeWidth={3.5} /> : null}
      </span>
      <span>
        Akceptuję{' '}
        <Link
          href={`/${locale}/regulamin`}
          target="_blank"
          rel="noopener"
          onClick={(e) => e.stopPropagation()}
          className={legal}
        >
          regulamin
        </Link>{' '}
        i{' '}
        <Link
          href={`/${locale}/polityka-prywatnosci`}
          target="_blank"
          rel="noopener"
          onClick={(e) => e.stopPropagation()}
          className={legal}
        >
          politykę prywatności
        </Link>
        .
      </span>
    </label>
  )
}
